import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../services/apiService';
import authService from '../services/authService';

const PaymentPage = () => {
  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const hasInitialFetch = useRef(false);

  // Fetch payments data
  const fetchPayments = useCallback(async (page = 1, status = '') => {
    try {
      setLoading(true);
      setError('');
      
      // Check authentication
      if (!authService.isAuthenticated()) {
        setError('Authentication required to view payments.');
        setLoading(false);
        return;
      }
      
      const params = {
        page: page,
        per_page: 10,
        ...(status && { status })
      };
      
      const response = await apiService.getPayments(params);
      
      if (response.success && response.data && response.data.data) {
        if (response.data.data.payments && Array.isArray(response.data.data.payments)) {
          setPayments(response.data.data.payments);
          setPagination(response.data.data.pagination);
        } else {
          setPayments([]);
          setPagination(response.data.data.pagination || {});
        }
      } else {
        setError(response.message || 'Failed to fetch payments');
      }
    } catch (err) {
      setError('Error fetching payments. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load payments on component mount
  useEffect(() => {
    if (!hasInitialFetch.current) {
      hasInitialFetch.current = true;
      fetchPayments();
    }
  }, [fetchPayments]);

  // Handle page change
  const handlePageChange = (page) => {
    fetchPayments(page);
  };


  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-success';
      case 'pending':
        return 'bg-warning';
      case 'failed':
        return 'bg-danger';
      case 'refunded':
        return 'bg-info';
      default:
        return 'bg-secondary';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return 'bi-check-circle-fill';
      case 'pending':
        return 'bi-clock-fill';
      case 'failed':
        return 'bi-x-circle-fill';
      case 'refunded':
        return 'bi-arrow-return-left';
      default:
        return 'bi-question-circle-fill';
    }
  };

  return (
    <>
     

      {/* Payment List Section */}
      <section className="section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="mb-4">
                <h3>Payment Summary</h3>
              </div>

              {/* Error Display */}
              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                </div>
              )}

              {/* Loading State */}
              {loading && (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Loading payments...</p>
                </div>
              )}

              {/* Payment Table */}
              {!loading && !error && (
                <>
                  {(!payments || payments.length === 0) ? (
                    <div className="text-center py-5">
                      <i className="bi bi-credit-card fs-1 text-muted mb-3"></i>
                      <h5 className="text-muted">No payments found</h5>
                      <p className="text-muted">You haven't made any payments yet.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>Payment ID</th>
                            <th>Order ID</th>
                            <th>Description</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Date</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments && payments.map((payment) => (
                            <tr key={payment.id}>
                              <td>
                                <code className="text-primary">{payment.payment_id}</code>
                              </td>
                              <td>
                                <code className="text-secondary">{payment.order_id}</code>
                              </td>
                              <td>{payment.description}</td>
                              <td>
                                <strong>{formatCurrency(payment.amount)}</strong>
                                <br />
                                <small className="text-muted">{payment.currency}</small>
                              </td>
                              <td>
                                <span className={`badge ${getStatusBadgeClass(payment.status)}`}>
                                  <i className={`bi ${getStatusIcon(payment.status)} me-1`}></i>
                                  {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                                </span>
                              </td>
                              <td>
                                <div>
                                  <div>{formatDate(payment.created_at)}</div>
                                  {payment.updated_at !== payment.created_at && (
                                    <small className="text-muted">
                                      Updated: {formatDate(payment.updated_at)}
                                    </small>
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="btn-group btn-group-sm">
                                  <button
                                    className="btn btn-outline-primary"
                                    title="View Details"
                                    onClick={() => {
                                      // You can implement a modal or expand row functionality here
                                      alert(`Payment Details:\n\nPayment ID: ${payment.payment_id}\nOrder ID: ${payment.order_id}\nAmount: ${formatCurrency(payment.amount)}\nStatus: ${payment.status}\nDescription: ${payment.description}`);
                                    }}
                                  >
                                    <i className="bi bi-eye"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Pagination */}
                  {pagination && pagination.last_page > 1 && (
                    <nav aria-label="Payment pagination" className="mt-4">
                      <ul className="pagination justify-content-center">
                        <li className={`page-item ${pagination.current_page === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(pagination.current_page - 1)}
                            disabled={pagination.current_page === 1}
                          >
                            Previous
                          </button>
                        </li>
                        
                        {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map((page) => (
                          <li key={page} className={`page-item ${page === pagination.current_page ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          </li>
                        ))}
                        
                        <li className={`page-item ${pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(pagination.current_page + 1)}
                            disabled={pagination.current_page === pagination.last_page}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PaymentPage;
